/**
 * CORE-874 — правила визарда создания программы, отдельно от рендера.
 *
 * Здесь всё, что легко сломать молча и невозможно заметить глазами: какой
 * шаг открыть у брошенного черновика, что именно уходит в частичный PATCH, и
 * когда сохранять не нужно вовсе.
 */
import type { MasterProgram, ProgramDraftPatch, ProgramProductType } from '../../../types/programs';

export type ProgramStepId = 1 | 2 | 3 | 4 | 5;

export interface ProgramStepDef {
  id: ProgramStepId;
  title: string;
  /** Тикет, который приносит этот шаг. Пока шага нет — так и написано в заглушке. */
  ticket: string;
}

/** Степпер слева (Макет 3). Все пять пунктов видны с первого шага — мастер должен понимать, во что ввязался. */
export const PROGRAM_STEPS: ProgramStepDef[] = [
  { id: 1, title: 'Основная информация', ticket: 'CORE-874' },
  { id: 2, title: 'Содержание', ticket: 'CORE-875' },
  { id: 3, title: 'Доступ и цена', ticket: 'CORE-876' },
  { id: 4, title: 'Дополнительно', ticket: 'CORE-909' },
  { id: 5, title: 'Предпросмотр и публикация', ticket: 'CORE-910' },
];

/**
 * CORE-993 — те же пять шагов у платной практики, но второй называется иначе.
 *
 * Шагов намеренно остаётся пять: «Шаг N из 5» обещан карточкой черновика
 * (Макет 1), и продукт, который считает шаги по-своему, читался бы как
 * сломанный черновик. Меняется только второй — у платной практики там не
 * конструктор разделов, а выбор одной практики, и называть его «Содержанием»
 * значит обещать составное содержание, которого у неё нет.
 */
export function programStepsFor(productType: ProgramProductType): ProgramStepDef[] {
  if (productType !== 'paidPractice') return PROGRAM_STEPS;
  return PROGRAM_STEPS.map((step) => (
    step.id === 2 ? { ...step, title: 'Практика', ticket: 'CORE-993' } : step
  ));
}

/** Заголовок визарда, пока у продукта нет названия. */
export function newProductTitle(productType: ProgramProductType): string {
  return productType === 'paidPractice' ? 'Новая практика' : 'Новая программа';
}

/** Три языка платформы — те же, что в переключателе интерфейса. */
export const PROGRAM_LANGUAGES: { code: string; label: string }[] = [
  { code: 'ru', label: 'Русский' },
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
];

export const PROGRAM_TITLE_MAX = 120;
export const PROGRAM_SHORT_MAX = 200;
export const PROGRAM_FULL_MAX = 5000;

/**
 * На каком шаге открыть черновик по кнопке «Продолжить».
 *
 * Мастер бросил визард после шага 3 — открывать надо шаг 4, то есть первый
 * незаконченный, а не тот, который он последним видел. Дошедшего до конца
 * возвращаем на шаг 5: дальше идти некуда, а «Предпросмотр и публикация» —
 * ровно то место, ради которого он вернулся.
 */
export function resumeStep(program: Pick<MasterProgram, 'lastCompletedStep'>): ProgramStepId {
  const done = program.lastCompletedStep ?? 0;
  if (done <= 0) return 1;
  if (done >= 5) return 5;
  return (done + 1) as ProgramStepId;
}

export interface BasicsForm {
  title: string;
  shortDescription: string;
  fullDescription: string;
  coverImageUrl: string | null;
  directions: string[];
  language: string | null;
}

/** Что показывать в форме, когда черновик уже частично заполнен. */
export function basicsFromProgram(program: MasterProgram): BasicsForm {
  return {
    title: program.title ?? '',
    shortDescription: program.shortDescription ?? '',
    fullDescription: program.fullDescription ?? '',
    coverImageUrl: program.coverImageUrl,
    directions: program.directions ?? [],
    language: program.language,
  };
}

/**
 * Тело PATCH для шага 1.
 *
 * Кладём только поля этого шага — сервер трактует отсутствие поля как «не
 * трогай», и лишний ключ здесь стёр бы то, что записал соседний шаг.
 *
 * `completedStep` проставляется только когда шаг действительно заполнен:
 * автосохранение на полпути не должно объявлять шаг завершённым, иначе
 * «Продолжить» у брошенного черновика уведёт мастера дальше, чем он дошёл.
 */
export function basicsPatch(form: BasicsForm, opts?: { completed?: boolean }): ProgramDraftPatch {
  const patch: ProgramDraftPatch = {
    title: form.title.trim(),
    shortDescription: form.shortDescription.trim(),
    fullDescription: form.fullDescription.trim(),
    directions: form.directions,
  };
  // null — это «обложки нет». Отправлять его нечем: у частичного PATCH нет
  // способа отличить очистку от «поля не касались», поэтому пустую обложку
  // просто не шлём, а очистка приедет вместе с CORE-910.
  if (form.coverImageUrl) patch.coverImageUrl = form.coverImageUrl;
  if (form.language) patch.language = form.language;
  if (opts?.completed) patch.completedStep = 1;
  return patch;
}

/**
 * Заполнен ли шаг настолько, чтобы пускать «Далее».
 *
 * Полное описание и обложку не требуем: карточку они украшают, но мастер
 * дописывает их позже, а отказ идти дальше из-за незалитой картинки — это
 * тупик там, где ТЗ обещает черновик.
 */
export function isBasicsComplete(form: BasicsForm): boolean {
  return form.title.trim().length > 0
    && form.shortDescription.trim().length > 0
    && form.directions.length > 0;
}

/** Чего не хватает — подсказка под кнопкой, а не молчаливая серая кнопка. */
export function basicsMissing(form: BasicsForm): string[] {
  const missing: string[] = [];
  if (!form.title.trim()) missing.push('название');
  if (!form.shortDescription.trim()) missing.push('короткое описание');
  if (form.directions.length === 0) missing.push('направление');
  return missing;
}

/**
 * Изменилось ли что-то по сравнению с тем, что уже лежит в черновике.
 * Автосохранение, которое шлёт PATCH на каждый фокус поля, ничего не меняя,
 * — это лишний трафик и лишние строки в UpdatedAtUtc, из-за которых
 * «Черновики» переупорядочиваются сами собой.
 */
export function basicsDirty(form: BasicsForm, program: MasterProgram): boolean {
  const same = (a: string, b: string | null) => a.trim() === (b ?? '').trim();
  return !(
    same(form.title, program.title)
    && same(form.shortDescription, program.shortDescription)
    && same(form.fullDescription, program.fullDescription)
    && (form.coverImageUrl ?? null) === (program.coverImageUrl ?? null)
    && (form.language ?? null) === (program.language ?? null)
    && form.directions.length === (program.directions ?? []).length
    && form.directions.every((d) => (program.directions ?? []).includes(d))
  );
}
